import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/i18n/LanguageContext';
import { generateBreadcrumbSchema, type BreadcrumbItem } from './breadcrumbSchema';
import { localizeInternalUrl } from '@/lib/languageRouting';
import type { Language } from '@/i18n/translations';
import { SITE_ORIGIN } from '@/lib/siteOrigin';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: 'website' | 'article';
  image?: string;
  imageAlt?: string;
  noIndex?: boolean;
  schema?: object;
  breadcrumbs?: BreadcrumbItem[];
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  keywords?: string[];
}

// Base URL for the site: the canonical origin defined in server/site-origin.ts
const BASE_URL = SITE_ORIGIN;

// Schema.org LocalBusiness - à utiliser sur toutes les pages
const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "ConsulTIC",
  "description": "Solutions IT, infrastructure sécurisée et support 24/7 pour entreprises en Europe",
  "url": BASE_URL,
  "telephone": "+32 2 880 70 70",
  "email": "contact@consultic.com",
  "image": `${BASE_URL}/og-image.png`,
  "logo": `${BASE_URL}/images/logo-email-horizontal.png`,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Boulevard Millenium 11",
    "addressLocality": "Houdeng-Goegnies",
    "postalCode": "7110",
    "addressCountry": "BE"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "50.4833",
    "longitude": "4.1500"
  },
  "openingHours": "Mo-Fr 08:30-17:30",
  "priceRange": "€€",
  "sameAs": [
    "https://www.linkedin.com/company/18288395",
    "https://www.facebook.com/consultic.be/",
    "https://www.youtube.com/@consulticeu",
    "https://share.google/3Hipl7Vhhb0M2uvKt"
  ]
};

export function SEOHead({ 
  title, 
  description, 
  canonical, 
  type = 'website',
  image = `${BASE_URL}/og-image.png`,
  imageAlt = 'ConsulTIC - Solutions IT managées pour PME',
  noIndex = false,
  schema,
  breadcrumbs,
  author = 'ConsulTIC',
  publishedTime,
  modifiedTime,
  section,
  keywords = []
}: SEOHeadProps) {
  const { language } = useLanguage();
  
  // Generate breadcrumb schema if breadcrumbs provided
  const breadcrumbSchema = breadcrumbs ? generateBreadcrumbSchema(breadcrumbs) : null;
  
  // Ensure title is under 60 characters
  const truncatedTitle = title.length > 55 ? `${title.substring(0, 52)}...` : title;
  const fullTitle = `${truncatedTitle} | ConsulTIC`;
  
  // Ensure description is under 160 characters
  const truncatedDescription = description.length > 160 ? `${description.substring(0, 157)}...` : description;
  
  // Build canonical URL - normalize to remove trailing slash
  const buildCanonicalUrl = (targetLanguage: Language) => {
    const path = !canonical || canonical === '/' ? '/' : canonical.startsWith('/') ? canonical : `/${canonical}`;
    const localizedPath = localizeInternalUrl(path, targetLanguage);
    return localizedPath === '/' ? `${BASE_URL}/` : `${BASE_URL}${localizedPath}`.replace(/\/$/, '');
  };
  const canonicalUrl = buildCanonicalUrl(language);
  const localizedBusinessSchema = localizeSchemaUrls(localBusinessSchema, language);
  const localizedSchema = schema ? localizeSchemaUrls(schema, language) : null;
  const localizedBreadcrumbSchema = breadcrumbSchema ? localizeSchemaUrls(breadcrumbSchema, language) : null;
  
  // Get locale for og:locale
  const getLocale = () => {
    switch (language) {
      case 'fr': return 'fr_BE';
      case 'nl': return 'nl_BE';
      case 'en': return 'en_US';
      default: return 'fr_BE';
    }
  };
  
  return (
    <Helmet>
      {/* Dynamic lang attribute */}
      <html lang={language} />
      
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDescription} />
      
      {/* Keywords if provided */}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      
      {/* Author */}
      <meta name="author" content={author} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Content Language */}
      <meta httpEquiv="content-language" content={language} />
      
      {/* Open Graph - Essential */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={getLocale()} />
      {(['fr', 'nl', 'en'] as const)
        .filter((alternateLanguage) => alternateLanguage !== language)
        .map((alternateLanguage) => (
          <meta key={alternateLanguage} property="og:locale:alternate" content={getOpenGraphLocale(alternateLanguage)} />
        ))}
      <meta property="og:site_name" content="ConsulTIC" />
      
      {/* Open Graph - Image with dimensions */}
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={imageAlt} />
      
      {/* Open Graph - Article specific */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {type === 'article' && section && (
        <meta property="article:section" content={section} />
      )}
      {type === 'article' && keywords.length > 0 && keywords.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Card - Summary Large Image */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@ConsulTIC" />
      <meta name="twitter:creator" content="@ConsulTIC" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      
      {/* LinkedIn specific */}
      <meta property="linkedin:owner" content="company/18288395" />
      
      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr-BE" href={buildCanonicalUrl('fr')} />
      <link rel="alternate" hrefLang="nl-BE" href={buildCanonicalUrl('nl')} />
      <link rel="alternate" hrefLang="en" href={buildCanonicalUrl('en')} />
      <link rel="alternate" hrefLang="x-default" href={buildCanonicalUrl('fr')} />
      
      {/* Schema.org LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify(localizedBusinessSchema)}
      </script>
      
      {/* Additional Schema if provided */}
      {localizedSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localizedSchema)}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {localizedBreadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(localizedBreadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
}

function getOpenGraphLocale(language: Language) {
  switch (language) {
    case 'fr': return 'fr_BE';
    case 'nl': return 'nl_BE';
    case 'en': return 'en_US';
  }
}

function localizeSchemaUrls(value: unknown, language: Language): unknown {
  if (typeof value === 'string') {
    if (value === BASE_URL || value.startsWith(`${BASE_URL}/`)) {
      const url = new URL(value);
      return `${BASE_URL}${localizeInternalUrl(`${url.pathname}${url.search}${url.hash}`, language)}`;
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => localizeSchemaUrls(item, language));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, localizeSchemaUrls(item, language)]),
    );
  }

  return value;
}
